import React, { useEffect, useMemo, useState } from "react";
import ApiURL from "../controllers/Api";
import CompanyLayout from "./CompanyLayout";
import EmployeeLayout from "./EmployeeLayout";
import ResumeDetailsModal from "./ResumeDetailsModal";
import { toast } from "../utils/toast";
import { formatKolkataDateTime } from "../utils/dateTime";

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem("token");
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const emptyTagDraft = {
  resume_id: null,
  sub_company_ids: [],
  requirement_ids: [],
  requirement_notes: "",
};

const emptyAssignDraft = {
  resume_id: null,
  employee_ids: [],
};

const csvEscape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const getAtsTone = (score) => {
  if (score >= 85) return "bg-emerald-500 text-emerald-700";
  if (score >= 70) return "bg-amber-500 text-amber-700";
  return "bg-rose-500 text-rose-700";
};

export default function CompanyUploadedResumes() {
  const currentUser = getStoredUser();
  const isEmployeeView = currentUser?.user_type === "employee" || currentUser?.role === "employee";
  const Layout = isEmployeeView ? EmployeeLayout : CompanyLayout;
  const [resumes, setResumes] = useState([]);
  const [subCompanies, setSubCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingTag, setSavingTag] = useState(false);
  const [savingAssign, setSavingAssign] = useState(false);
  const [tagDraft, setTagDraft] = useState(emptyTagDraft);
  const [assignDraft, setAssignDraft] = useState(emptyAssignDraft);
  const [viewResume, setViewResume] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    experienceRange: "",
    status: "",
    atsRange: "",
    sortBy: "newest",
    perPage: 10,
  });
  const [page, setPage] = useState(1);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [resumeRes, subCompanyRes, employeeRes] = await Promise.all([
        fetch(`${ApiURL}get_visible_resumes.php`, { headers }),
        fetch(`${ApiURL}crm/company/get_sub_companies.php`, { headers }),
        fetch(`${ApiURL}crm/admin/get_employees.php`, { headers }),
      ]);

      const resumeJson = await resumeRes.json().catch(() => ({}));
      const subCompanyJson = await subCompanyRes.json().catch(() => ({}));
      const employeeJson = await employeeRes.json().catch(() => ({}));

      if (!resumeRes.ok || resumeJson.success === false) {
        throw new Error(resumeJson.message || "Failed to load resumes");
      }
      if (!subCompanyRes.ok || subCompanyJson.success === false) {
        throw new Error(subCompanyJson.message || "Failed to load sub companies");
      }
      if (!employeeRes.ok || employeeJson.success === false) {
        throw new Error(employeeJson.message || "Failed to load recruiters");
      }

      setResumes(Array.isArray(resumeJson.data) ? resumeJson.data : []);
      setSubCompanies(subCompanyJson?.data?.sub_companies || []);
      setEmployees(employeeJson?.data?.employees || []);
    } catch (error) {
      console.error("Error loading resume library:", error);
      toast.error(error.message || "Failed to load resume library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const categories = useMemo(() => {
    return Array.from(
      new Set(
        resumes
          .map((resume) => resume.extracted_category || resume.category || "")
          .map((value) => value.trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [resumes]);

  const activeEmployees = useMemo(
    () => employees.filter((employee) => employee.status === "active"),
    [employees]
  );

  const selectedTagSubCompanyIds = useMemo(
    () => new Set((tagDraft.sub_company_ids || []).map(String)),
    [tagDraft.sub_company_ids]
  );

  const requirementOptions = useMemo(() => {
    const selectedCompanyIds = selectedTagSubCompanyIds;
    return subCompanies.flatMap((company) =>
      (company.requirements || [])
        .filter(() => selectedCompanyIds.size === 0 || selectedCompanyIds.has(String(company.id)))
        .map((requirement) => ({
          ...requirement,
          sub_company_id: company.id,
          sub_company_name: company.name,
        }))
    );
  }, [selectedTagSubCompanyIds, subCompanies]);

  const filteredResumes = useMemo(() => {
    let next = [...resumes];

    if (filters.search) {
      const term = filters.search.toLowerCase();
      next = next.filter((resume) => {
        const skills = Array.isArray(resume.skills) ? resume.skills.join(", ") : "";
        return [
          resume.id,
          resume.original_filename,
          resume.extracted_name,
          resume.extracted_email,
          resume.email,
          resume.extracted_phone,
          resume.phone,
          resume.extracted_job_title,
          resume.job_title,
          resume.extracted_category,
          resume.category,
          resume.current_company,
          resume.location,
          resume.uploaded_by_name,
          ...(resume.sub_company_names || []),
          ...(resume.requirement_names || []),
          ...(resume.assigned_employee_names || []),
          skills,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      });
    }

    if (filters.category) {
      next = next.filter((resume) => (resume.extracted_category || resume.category || "") === filters.category);
    }

    if (filters.status) {
      next = next.filter((resume) => {
        if (filters.status === "tagged") return (resume.sub_company_ids || []).length > 0;
        if (filters.status === "assigned") return (resume.assigned_employee_ids || []).length > 0;
        if (filters.status === "hired") return Boolean(resume.is_hired);
        return true;
      });
    }

    if (filters.atsRange) {
      next = next.filter((resume) => {
        const ats = Number(resume.ats_score || 0);
        if (filters.atsRange === "80+") return ats >= 80;
        if (filters.atsRange === "60-79") return ats >= 60 && ats < 80;
        if (filters.atsRange === "0-59") return ats < 60;
        return true;
      });
    }

    if (filters.experienceRange) {
      next = next.filter((resume) => {
        const experience = Number(resume.experience_years || 0);
        if (filters.experienceRange === "0-1") return experience <= 1;
        if (filters.experienceRange === "1-3") return experience >= 1 && experience <= 3;
        if (filters.experienceRange === "3-5") return experience >= 3 && experience <= 5;
        if (filters.experienceRange === "5+") return experience >= 5;
        return true;
      });
    }

    next.sort((left, right) => {
      if (filters.sortBy === "ats") {
        return Number(right.ats_score || 0) - Number(left.ats_score || 0);
      }
      if (filters.sortBy === "oldest") {
        return new Date(left.upload_date || 0).getTime() - new Date(right.upload_date || 0).getTime();
      }
      return new Date(right.upload_date || 0).getTime() - new Date(left.upload_date || 0).getTime();
    });

    return next;
  }, [filters, resumes]);

  const totalPages = Math.max(1, Math.ceil(filteredResumes.length / Number(filters.perPage || 10)));
  const paginatedResumes = filteredResumes.slice((page - 1) * filters.perPage, page * filters.perPage);

  const resetFilters = () => {
    setFilters({
      search: "",
      category: "",
      experienceRange: "",
      status: "",
      atsRange: "",
      sortBy: "newest",
      perPage: 10,
    });
  };

  const openTagModal = (resume) => {
    setTagDraft({
      resume_id: resume.id,
      sub_company_ids: (resume.sub_company_ids || []).map(String),
      requirement_ids: (resume.requirement_ids || []).map(String),
      requirement_notes: resume.requirement_notes || "",
    });
  };

  const openAssignModal = (resume) => {
    if (!(resume.requirement_ids || []).length) {
      toast.warning("Tag the resume to a company requirement before assigning.");
      openTagModal(resume);
      return;
    }
    setAssignDraft({
      resume_id: resume.id,
      employee_ids: (resume.assigned_employee_ids || []).map(String),
    });
  };

  const saveTag = async () => {
    if (!tagDraft.resume_id) return;
    if (!tagDraft.sub_company_ids.length && !tagDraft.requirement_ids.length) {
      toast.warning("Please select a sub company or requirement.");
      return;
    }

    setSavingTag(true);
    try {
      const response = await fetch(`${ApiURL}crm/company/save_resume_requirement.php`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          resume_id: tagDraft.resume_id,
          sub_company_ids: tagDraft.sub_company_ids.map(Number),
          requirement_ids: tagDraft.requirement_ids.map(Number),
          requirement_notes: tagDraft.requirement_notes,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to save resume tags");
      }
      toast.success("Resume tagged successfully.");
      setTagDraft(emptyTagDraft);
      await loadData();
    } catch (error) {
      toast.error(error.message || "Failed to save resume tags");
    } finally {
      setSavingTag(false);
    }
  };

  const saveAssignment = async () => {
    if (!assignDraft.resume_id) return;
    if (!assignDraft.employee_ids.length) {
      toast.warning("Please select at least one recruiter.");
      return;
    }

    setSavingAssign(true);
    try {
      const response = await fetch(`${ApiURL}crm/admin/assign_resume.php`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          resume_id: assignDraft.resume_id,
          employee_ids: assignDraft.employee_ids.map(Number),
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to assign resume");
      }
      toast.success("Resume assigned successfully.");
      setAssignDraft(emptyAssignDraft);
      await loadData();
    } catch (error) {
      toast.error(error.message || "Failed to assign resume");
    } finally {
      setSavingAssign(false);
    }
  };

  const handleViewResume = (resume) => {
    setViewResume(resume);
  };

  const handleDownloadResume = async (resume) => {
    try {
      const response = await fetch(`${ApiURL}admin/download_resume.php?resume_id=${resume.id}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) throw new Error("Unable to download resume");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = resume.original_filename || "resume.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message || "Failed to download resume");
    }
  };

  const exportCsv = () => {
    const headers = [
      "ID",
      "File",
      "Candidate",
      "Email",
      "Phone",
      "Job Title",
      "Category",
      "Tagged Company",
      "Requirement",
      "Recruiters",
      "Experience",
      "ATS",
      "Uploaded",
    ];
    const rows = filteredResumes.map((resume) => [
      resume.id,
      resume.original_filename,
      resume.extracted_name || "",
      resume.extracted_email || resume.email || "",
      resume.extracted_phone || resume.phone || "",
      resume.extracted_job_title || resume.job_title || "",
      resume.extracted_category || resume.category || "",
      (resume.sub_company_names || []).join(", "),
      (resume.requirement_names || []).join(", "),
      (resume.assigned_employee_names || []).join(", "),
      resume.experience_years || 0,
      resume.ats_score || 0,
      formatKolkataDateTime(resume.upload_date),
    ]);

    const csv = [headers, ...rows].map((row) => row.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" }); 
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `resume_library_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Layout
      title="Resume Library"
      description="Tag resumes to sub companies and requirements, then assign recruiters for the next process."
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={exportCsv}
            className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
          >
            Export CSV ({filteredResumes.length} resumes)
          </button>
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700">
            Showing: {loading ? "Loading..." : `${filteredResumes.length} visible`}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="grid gap-3 md:grid-cols-3">
            <input
              type="text"
              value={filters.search}
              onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
              className="rounded-xl border border-gray-300 px-3 py-2 text-sm"
              placeholder="Search file, name, email, phone, company, skill..."
            />
            <select
              value={filters.category}
              onChange={(event) => setFilters((prev) => ({ ...prev, category: event.target.value }))}
              className="rounded-xl border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">All categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <select
              value={filters.experienceRange}
              onChange={(event) => setFilters((prev) => ({ ...prev, experienceRange: event.target.value }))}
              className="rounded-xl border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">All experience</option>
              <option value="0-1">0-1 years</option>
              <option value="1-3">1-3 years</option>
              <option value="3-5">3-5 years</option>
              <option value="5+">5+ years</option>
            </select>
            <select
              value={filters.atsRange}
              onChange={(event) => setFilters((prev) => ({ ...prev, atsRange: event.target.value }))}
              className="rounded-xl border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">All ATS scores</option>
              <option value="80+">80% and above</option>
              <option value="60-79">60% to 79%</option>
              <option value="0-59">Below 60%</option>
            </select>
            <select
              value={filters.status}
              onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
              className="rounded-xl border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">All statuses</option>
              <option value="tagged">Tagged</option>
              <option value="assigned">Assigned</option>
              <option value="hired">Hired</option>
            </select>
            <div className="flex gap-2">
              <select
                value={filters.sortBy}
                onChange={(event) => setFilters((prev) => ({ ...prev, sortBy: event.target.value }))}
                className="min-w-0 flex-1 rounded-xl border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="ats">Highest ATS</option>
              </select>
              <button
                type="button"
                onClick={resetFilters}
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-gray-200 bg-gray-50 text-slate-600">
                <tr>
                  <th className="px-3 py-3 text-left font-medium">ID</th>
                  <th className="px-3 py-3 text-left font-medium">File</th>
                  <th className="px-3 py-3 text-left font-medium">Candidate</th>
                  <th className="px-3 py-3 text-left font-medium">Email</th>
                  <th className="px-3 py-3 text-left font-medium">Phone</th>
                  <th className="px-3 py-3 text-left font-medium">Job Title</th>
                  <th className="px-3 py-3 text-left font-medium">Category</th>
                  <th className="px-3 py-3 text-left font-medium">Skills</th>
                  <th className="px-3 py-3 text-left font-medium">Tagged Company</th>
                  <th className="px-3 py-3 text-left font-medium">Recruiter</th>
                  <th className="px-3 py-3 text-left font-medium">Exp</th>
                  <th className="px-3 py-3 text-left font-medium">ATS</th>
                  <th className="px-3 py-3 text-left font-medium">Uploaded</th>
                  <th className="px-3 py-3 text-left font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedResumes.map((resume) => {
                  const ats = Number(resume.ats_score || 0);
                  const atsTone = getAtsTone(ats);
                  return (
                    <tr key={resume.id} className="hover:bg-gray-50 align-top">
                      <td className="px-3 py-3 text-slate-600">#{resume.id}</td>
                      <td className="px-3 py-3">
                        <div className="max-w-[190px] truncate font-medium text-slate-900" title={resume.original_filename}>
                          {resume.original_filename || "-"}
                        </div>
                        <div className="mt-1 text-[10px] text-slate-500">{resume.resume_source_label || ""}</div>
                      </td>
                      <td className="px-3 py-3 font-medium text-slate-900">
                        {resume.extracted_name || "Candidate"}
                      </td>
                      <td className="px-3 py-3 text-slate-600">
                        <div className="max-w-[170px] truncate" title={resume.extracted_email || resume.email || ""}>
                          {resume.extracted_email || resume.email || "-"}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-slate-600">{resume.extracted_phone || resume.phone || "N/A"}</td>
                      <td className="px-3 py-3 text-slate-600">{resume.extracted_job_title || resume.job_title || "-"}</td>
                      <td className="px-3 py-3">
                        <span className="rounded bg-blue-50 px-2 py-1 text-[10px] font-medium text-blue-700">
                          {resume.extracted_category || resume.category || "-"}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex max-w-[180px] flex-wrap gap-1">
                          {(resume.skills || []).slice(0, 4).map((skill, idx) => (
                            <span key={idx} className="inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700 ring-1 ring-inset ring-violet-200/50">
                              {skill}
                            </span>
                          ))}
                          {(resume.skills || []).length > 4 && (
                            <span className="text-[10px] text-slate-400">+{resume.skills.length - 4} more</span>
                          )}
                          {(!resume.skills || resume.skills.length === 0) && (
                            <span className="text-[10px] text-slate-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-slate-600">
                        <div>{resume.sub_company_names?.join(", ") || "-"}</div>
                        {resume.requirement_names?.length ? (
                          <div className="mt-1 text-[10px] text-slate-500">{resume.requirement_names.join(", ")}</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 text-slate-600">
                        {resume.assigned_employee_names?.length ? resume.assigned_employee_names.join(", ") : "-"}
                      </td>
                      <td className="px-3 py-3 text-slate-600">{resume.experience_years || 0}y</td>
                      <td className="px-3 py-3">
                        <div className="flex min-w-[90px] items-center gap-2">
                          <div className="h-2 flex-1 rounded-full bg-slate-100">
                            <div className={`h-2 rounded-full ${atsTone.split(" ")[0]}`} style={{ width: `${Math.min(100, ats)}%` }} />
                          </div>
                          <span className={`text-[10px] font-semibold ${atsTone.split(" ")[1]}`}>{ats}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-slate-500">{formatKolkataDateTime(resume.upload_date)}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          <button type="button" onClick={() => handleViewResume(resume)} className="rounded-lg bg-blue-50 px-2 py-1 text-[10px] font-semibold text-blue-700 hover:bg-blue-100">View</button>
                          <button type="button" onClick={() => openTagModal(resume)} className="rounded-lg bg-amber-50 px-2 py-1 text-[10px] font-semibold text-amber-700 hover:bg-amber-100">Tag</button>
                          <button type="button" onClick={() => openAssignModal(resume)} disabled={resume.is_hired} className="rounded-lg bg-cyan-50 px-2 py-1 text-[10px] font-semibold text-cyan-700 hover:bg-cyan-100 disabled:opacity-50">Assign</button>
                          <button type="button" onClick={() => handleDownloadResume(resume)} className="rounded-lg bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-100">Download</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!loading && paginatedResumes.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="px-3 py-10 text-center text-slate-500">
                      No resumes match this view.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-4 py-3">
            <div className="text-xs text-slate-500">
              Page {page} of {totalPages} - {filteredResumes.length} resume(s)
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Show</span>
              <select
                value={filters.perPage}
                onChange={(event) => setFilters((prev) => ({ ...prev, perPage: Number(event.target.value) }))}
                className="rounded-lg border border-gray-300 px-2 py-1 text-xs"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <button type="button" onClick={() => setPage((prev) => Math.max(1, prev - 1))} disabled={page <= 1} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-40">Prev</button>
              <button type="button" onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} disabled={page >= totalPages} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-40">Next</button>
            </div>
          </div>
        </div>
      </div>

      {tagDraft.resume_id ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
            <div className="border-b border-gray-200 px-6 py-5">
              <h3 className="text-lg font-semibold text-slate-900">Tag Resume</h3>
              <p className="mt-1 text-sm text-slate-500">Select sub company and requirement for further process.</p>
            </div>
            <div className="space-y-4 px-6 py-5">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Sub Companies</label>
                <select
                  multiple
                  value={tagDraft.sub_company_ids}
                  onChange={(event) =>
                    setTagDraft((prev) => ({
                      ...prev,
                      sub_company_ids: Array.from(event.target.selectedOptions, (option) => option.value),
                      requirement_ids: [],
                    }))
                  }
                  className="h-36 w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                >
                  {subCompanies.map((company) => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
                <div className="mt-1 text-xs text-slate-400">Hold Ctrl/Cmd to select multiple.</div>
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Requirements</label>
                <select
                  multiple
                  value={tagDraft.requirement_ids}
                  onChange={(event) =>
                    setTagDraft((prev) => ({
                      ...prev,
                      requirement_ids: Array.from(event.target.selectedOptions, (option) => option.value),
                    }))
                  }
                  className="h-36 w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                >
                  {requirementOptions.map((requirement) => (
                    <option key={requirement.id} value={requirement.id}>
                      {requirement.sub_company_name} - {requirement.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Notes</label>
                <textarea
                  rows={3}
                  value={tagDraft.requirement_notes}
                  onChange={(event) => setTagDraft((prev) => ({ ...prev, requirement_notes: event.target.value }))}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-5">
              <button type="button" onClick={() => setTagDraft(emptyTagDraft)} className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={saveTag} disabled={savingTag} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                {savingTag ? "Saving..." : "Save Tag"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {assignDraft.resume_id ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
            <div className="border-b border-gray-200 px-6 py-5">
              <h3 className="text-lg font-semibold text-slate-900">Assign Recruiters</h3>
              <p className="mt-1 text-sm text-slate-500">Assign selected recruiters to all tagged requirements on this resume.</p>
            </div>
            <div className="px-6 py-5">
              <select
                multiple
                value={assignDraft.employee_ids}
                onChange={(event) =>
                  setAssignDraft((prev) => ({
                    ...prev,
                    employee_ids: Array.from(event.target.selectedOptions, (option) => option.value),
                  }))
                }
                className="h-56 w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
              >
                {activeEmployees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name} ({employee.personnel_id || employee.email})
                  </option>
                ))}
              </select>
              <div className="mt-1 text-xs text-slate-400">Hold Ctrl/Cmd to select multiple recruiters.</div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-5">
              <button type="button" onClick={() => setAssignDraft(emptyAssignDraft)} className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={saveAssignment} disabled={savingAssign} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                {savingAssign ? "Assigning..." : "Assign"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {viewResume ? (
        <ResumeDetailsModal resume={viewResume} onClose={() => setViewResume(null)} />
      ) : null}
    </Layout>
  );
}
